import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ConfirmDeleteModal } from '@/components';
import { StopFollowUpConfirmModal } from '../../components/StopFollowUpConfirmModal';
import { ActionConfirmModal } from '../../components/ActionConfirmModal';
import {
  DashboardTable,
  DashboardTableHeader,
  DashboardTableBody,
  DashboardTableRow,
  DashboardTableCell,
  TableChip,
} from '../../components/PremiumDesign';
import { EyeIcon } from '../../components/Icons';
import trashIcon from '../../assets/trashIcon.png';
import pauseCircleIcon from '../../assets/leads/pauseCircle.png';
import rescheduleIcon from '../../assets/leads/reschedule.png';
import playIcon from '../../assets/leads/play.png';
import pauseButtonIcon from '../../assets/leads/pauseButton.png';
import {
  getCRMContactFollowUps,
  deleteCRMContactFollowUp,
  pauseCRMContactFollowUp,
  resumeCRMContactFollowUp,
  rescheduleCRMContactFollowUp,
  stopCRMContactFollowUp,
} from '../api';
import { CRMFollowUpDetailModal } from './CRMFollowUpDetailModal';
import { RescheduleModal } from './RescheduleModal';
import { getFollowUpStatusChipStyle } from '../utils/tasksAndFollowups';
import { parseApiError } from '@/common/utils';

const PREVIEW_COUNT = 5;

function formatDate(dateString) {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function CRMFollowUpsOverviewTable({
  companyId,
  contactId,
  readOnly = false,
  canDelete = false,
  onViewAll,
}) {
  const queryClient = useQueryClient();
  const [viewingFollowUpId, setViewingFollowUpId] = useState(null);
  const [actionPopup, setActionPopup] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['crm-contact-follow-ups-preview', companyId, contactId],
    queryFn: () =>
      getCRMContactFollowUps({
        companyId,
        contactId,
        params: { page: 1, page_size: PREVIEW_COUNT },
      }),
    enabled: !!companyId && !!contactId,
  });

  const followUps = (Array.isArray(data) ? data : data?.results || []).slice(
    0,
    PREVIEW_COUNT,
  );

  const invalidateFollowUps = () => {
    queryClient.invalidateQueries({
      queryKey: ['crm-contact-follow-ups-preview', companyId, contactId],
    });
    queryClient.invalidateQueries({
      queryKey: ['crm-contact-follow-ups', companyId, contactId],
    });
  };

  const { mutate: handlePauseFollowUp, isPending: isPausing } = useMutation({
    mutationFn: pauseCRMContactFollowUp,
    onSuccess: () => {
      toast.success('Follow-up paused successfully!');
      setActionPopup(null);
      invalidateFollowUps();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to pause follow-up.');
    },
  });

  const { mutate: handleResumeFollowUp, isPending: isResuming } = useMutation({
    mutationFn: resumeCRMContactFollowUp,
    onSuccess: () => {
      toast.success('Follow-up resumed successfully!');
      setActionPopup(null);
      invalidateFollowUps();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to resume follow-up.');
    },
  });

  const { mutate: handleStopFollowUp, isPending: isStopping } = useMutation({
    mutationFn: stopCRMContactFollowUp,
    onSuccess: () => {
      toast.success('Follow-up stopped successfully!');
      setActionPopup(null);
      invalidateFollowUps();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to stop follow-up.');
    },
  });

  const { mutate: handleRescheduleFollowUp, isPending: isRescheduling } =
    useMutation({
      mutationFn: rescheduleCRMContactFollowUp,
      onSuccess: () => {
        toast.success('Follow-up rescheduled successfully!');
        setActionPopup(null);
        invalidateFollowUps();
      },
      onError: (err) => {
        const { message } = parseApiError(err);
        toast.error(message || 'Failed to reschedule follow-up.');
      },
    });

  const { mutate: handleDeleteFollowUp, isPending: isDeleting } = useMutation({
    mutationFn: (vars) => {
      if (!canDelete) throw new Error('Permission denied.');
      return deleteCRMContactFollowUp(vars);
    },
    onSuccess: () => {
      toast.success('Follow-up deleted successfully!');
      setActionPopup(null);
      invalidateFollowUps();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to delete follow-up.');
    },
  });

  return (
    <div className="bg-[#F5F5F5] rounded-b-[16px] p-[14px] sm:p-[14px] flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-[18px] font-medium text-[#50AB3D]">Follow-Ups</h3>
        {onViewAll && (
          <button
            type="button"
            onClick={onViewAll}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-[10px] text-sm font-medium text-white transition-colors hover:brightness-95 focus:outline-none"
            style={{ backgroundColor: '#50AB3D' }}
          >
            View All
            <span aria-hidden="true">→</span>
          </button>
        )}
      </div>

      <div>
        <DashboardTable>
          <DashboardTableHeader>
            <th className="py-3 px-4 text-left">Template Name</th>
            <th className="py-3 px-4 text-left">Template ID</th>
            <th className="py-3 px-4 text-left">Follow-Up Type</th>
            <th className="py-3 px-4 text-left">Message Type</th>
            <th className="py-3 px-4 text-left">Attempts</th>
            <th className="py-3 px-4 text-left">Next Execution</th>
            <th className="py-3 px-4 text-left">Scheduled For</th>
            <th className="py-3 px-4 text-left">Status</th>
            <th className="py-3 px-4 text-right">Action</th>
          </DashboardTableHeader>
          <DashboardTableBody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <DashboardTableRow
                  key={`skeleton-${i}`}
                  className="!cursor-default"
                >
                  <td className="py-3 px-4">
                    <div className="h-4 w-24 rounded bg-[#EBF4EA] animate-pulse" />
                  </td>
                  <td className="py-3 px-4">
                    <div className="h-4 w-20 rounded bg-[#EBF4EA] animate-pulse" />
                  </td>
                  <td className="py-3 px-4">
                    <div className="h-4 w-16 rounded bg-[#EBF4EA] animate-pulse" />
                  </td>
                  <td className="py-3 px-4">
                    <div className="h-4 w-16 rounded bg-[#EBF4EA] animate-pulse" />
                  </td>
                  <td className="py-3 px-4">
                    <div className="h-4 w-10 rounded bg-[#EBF4EA] animate-pulse" />
                  </td>
                  <td className="py-3 px-4">
                    <div className="h-4 w-28 rounded bg-[#EBF4EA] animate-pulse" />
                  </td>
                  <td className="py-3 px-4">
                    <div className="h-4 w-28 rounded bg-[#EBF4EA] animate-pulse" />
                  </td>
                  <td className="py-3 px-4">
                    <div className="h-6 w-20 rounded-full bg-[#EBF4EA] animate-pulse" />
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end gap-1">
                      <div className="h-9 w-9 rounded-lg bg-[#EBF4EA] animate-pulse" />
                      <div className="h-9 w-9 rounded-lg bg-[#EBF4EA] animate-pulse" />
                    </div>
                  </td>
                </DashboardTableRow>
              ))
            ) : followUps.length > 0 ? (
              followUps.map((followUp) => (
                <DashboardTableRow
                  key={followUp.id}
                  onClick={() => setViewingFollowUpId(followUp.id)}
                >
                  <DashboardTableCell>
                    {followUp.channel === 'email'
                      ? followUp.email_subject ||
                        followUp.standard_message_body ||
                        'Email Follow-up'
                      : followUp.message_type === 'template'
                        ? followUp.template_name || 'Template Follow-up'
                        : 'Custom Message'}
                  </DashboardTableCell>
                  <DashboardTableCell>
                    {followUp.template_id || '-'}
                  </DashboardTableCell>
                  <DashboardTableCell className="capitalize">
                    {followUp.follow_up_type || '-'}
                  </DashboardTableCell>
                  <DashboardTableCell className="capitalize">
                    {followUp.message_type || '-'}
                  </DashboardTableCell>
                  <DashboardTableCell>
                    {followUp.completed_attempts || 0}/
                    {followUp.max_attempts > 0 ? followUp.max_attempts : '∞'}
                  </DashboardTableCell>
                  <DashboardTableCell>
                    {followUp.follow_up_type === 'interval'
                      ? formatDate(followUp.next_execution_time)
                      : '-'}
                  </DashboardTableCell>
                  <DashboardTableCell>
                    {followUp.follow_up_type === 'scheduled'
                      ? formatDate(followUp.scheduled_date_time)
                      : '-'}
                  </DashboardTableCell>
                  <td className="py-3 px-4 align-middle">
                    {followUp.status ? (
                      <TableChip
                        {...getFollowUpStatusChipStyle(followUp.status)}
                      >
                        {followUp.status}
                      </TableChip>
                    ) : (
                      <span className="text-[#868686]">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4 align-middle text-right whitespace-nowrap">
                    <div
                      className="flex items-center justify-end gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!readOnly &&
                        (() => {
                          const status = String(
                            followUp?.status || '',
                          ).toLowerCase();
                          if (status === 'scheduled') {
                            return (
                              <button
                                type="button"
                                onClick={() =>
                                  setActionPopup({
                                    type: 'stop',
                                    id: followUp.id,
                                  })
                                }
                                disabled={isStopping}
                                className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg hover:bg-yellow-500/10 transition-colors focus:outline-none disabled:opacity-50"
                                title="Stop Follow-up"
                              >
                                <img
                                  src={pauseCircleIcon}
                                  alt="Stop"
                                  className="w-5 h-5 object-contain shrink-0"
                                />
                              </button>
                            );
                          }
                          if (status === 'stopped' || status === 'failed') {
                            return (
                              <button
                                type="button"
                                onClick={() =>
                                  setActionPopup({
                                    type: 'reschedule',
                                    id: followUp.id,
                                  })
                                }
                                disabled={isRescheduling}
                                className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg hover:bg-purple-500/10 transition-colors focus:outline-none disabled:opacity-50"
                                title="Reschedule Follow-up"
                              >
                                <img
                                  src={rescheduleIcon}
                                  alt="Reschedule"
                                  className="w-5 h-5 object-contain shrink-0"
                                />
                              </button>
                            );
                          }
                          if (status === 'paused') {
                            return (
                              <button
                                type="button"
                                onClick={() =>
                                  setActionPopup({
                                    type: 'resume',
                                    id: followUp.id,
                                  })
                                }
                                disabled={isResuming}
                                className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg hover:bg-green-500/10 transition-colors focus:outline-none disabled:opacity-50"
                                title="Start Follow-up"
                              >
                                <img
                                  src={playIcon}
                                  alt="Start"
                                  className="w-5 h-5 object-contain shrink-0"
                                />
                              </button>
                            );
                          }
                          if (status === 'active' || status === 'running') {
                            return (
                              <button
                                type="button"
                                onClick={() =>
                                  setActionPopup({
                                    type: 'pause',
                                    id: followUp.id,
                                  })
                                }
                                disabled={isPausing}
                                className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg hover:bg-green-500/10 transition-colors focus:outline-none disabled:opacity-50"
                                title="Pause Follow-up"
                              >
                                <img
                                  src={pauseButtonIcon}
                                  alt="Pause"
                                  className="w-5 h-5 object-contain shrink-0"
                                />
                              </button>
                            );
                          }
                          return null;
                        })()}
                      <button
                        type="button"
                        onClick={() => setViewingFollowUpId(followUp.id)}
                        className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg text-blue-500 hover:bg-blue-500/10 transition-colors focus:outline-none"
                        title="View Follow-up"
                      >
                        <EyeIcon className="w-5 h-5 shrink-0" />
                      </button>
                      {!readOnly && canDelete && (
                        <button
                          type="button"
                          onClick={() =>
                            setActionPopup({
                              type: 'delete',
                              id: followUp.id,
                            })
                          }
                          className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg hover:bg-red-500/10 transition-colors focus:outline-none"
                          title="Delete Follow-up"
                        >
                          <img
                            src={trashIcon}
                            alt=""
                            className="w-5 h-5 object-contain shrink-0"
                          />
                        </button>
                      )}
                    </div>
                  </td>
                </DashboardTableRow>
              ))
            ) : (
              <DashboardTableRow className="!cursor-default">
                <td
                  colSpan={9}
                  className="py-10 text-center text-sm text-[#868686]"
                >
                  No follow-ups found.
                </td>
              </DashboardTableRow>
            )}
          </DashboardTableBody>
        </DashboardTable>
      </div>

      <CRMFollowUpDetailModal
        companyId={companyId}
        contactId={contactId}
        followUpId={viewingFollowUpId}
        onClose={() => setViewingFollowUpId(null)}
        readOnly
      />

      <StopFollowUpConfirmModal
        isOpen={actionPopup?.type === 'stop'}
        onCancel={() => {
          if (!isStopping) setActionPopup(null);
        }}
        onConfirm={() =>
          handleStopFollowUp({
            companyId,
            contactId,
            followUpId: actionPopup?.id,
          })
        }
        isPending={isStopping}
      />

      <ConfirmDeleteModal
        isOpen={actionPopup?.type === 'delete'}
        title="Delete Follow-up"
        description="This action cannot be undone and it will not execute anymore."
        onCancel={() => {
          if (!isDeleting) setActionPopup(null);
        }}
        onConfirm={() =>
          handleDeleteFollowUp({
            companyId,
            contactId,
            followUpId: actionPopup?.id,
          })
        }
        isPending={isDeleting}
      />

      <RescheduleModal
        isOpen={actionPopup?.type === 'reschedule'}
        onClose={() => {
          if (!isRescheduling) setActionPopup(null);
        }}
        onReschedule={(newDate) =>
          handleRescheduleFollowUp({
            companyId,
            contactId,
            followUpId: actionPopup?.id,
            payload: { new_date_time: newDate },
          })
        }
        isRescheduling={isRescheduling}
      />

      <ActionConfirmModal
        isOpen={actionPopup?.type === 'pause'}
        onCancel={() => {
          if (!isPausing) setActionPopup(null);
        }}
        onConfirm={() =>
          handlePauseFollowUp({
            companyId,
            contactId,
            followUpId: actionPopup?.id,
          })
        }
        isPending={isPausing}
        title="Pause Follow-up"
        description="This follow-up will pause until you resume it."
        confirmLabel="Pause"
        pendingLabel="Pausing..."
      />

      <ActionConfirmModal
        isOpen={actionPopup?.type === 'resume'}
        onCancel={() => {
          if (!isResuming) setActionPopup(null);
        }}
        onConfirm={() =>
          handleResumeFollowUp({
            companyId,
            contactId,
            followUpId: actionPopup?.id,
          })
        }
        isPending={isResuming}
        title="Resume Follow-up"
        description="This follow-up will start running again on its schedule."
        confirmLabel="Resume"
        pendingLabel="Resuming..."
      />
    </div>
  );
}
